import { gql } from '@apollo/client/core';
import Tracking from '@/components/commons/Tracking.js';
import { updateFavoriteHeader, isUser, executeQuery } from '@/components/commons/utils.js';
import LocalStorage, { LocalStorageKeys } from '../components/commons/LocalStorage.js';
import { computed, reactive, toRefs } from '@vue/composition-api';
import UserService from '@/services/UserService.js';
import useMutation from './useMutation.js';

let favoritesData = LocalStorage.get(LocalStorageKeys.FAVORITES);

if (favoritesData && isUser()) {
  favoritesData = JSON.parse(favoritesData);
} else {
  favoritesData = {};
}

const state = reactive({
  client: true,
  favProduct: {},
  favorites: favoritesData,
  favoriteData: {},
  showSnackbar: false,
  targetDomEl: null,
});

export default function useFavorite() {
  function sendAddEvent(productId) {
    Tracking.track('add-to-favorites', {
      productId,
    });
  }

  function sendRemoveEvent(productId) {
    Tracking.track('remove-from-favorites', {
      productId,
    });
  }

  function updateFavorites(payload = []) {
    const data = {};
    payload.forEach((value) => {
      data[value] = 1;
    });

    state.favorites = { ...data };

    if (state.favorites && Object.keys(state.favorites)) {
      LocalStorage.set(LocalStorageKeys.FAVORITES, JSON.stringify(state.favorites));
    } else {
      LocalStorage.remove(LocalStorageKeys.FAVORITES);
    }

    LocalStorage.set(LocalStorageKeys.FAVORITE_TIME, new Date().getTime());
    updateFavoriteHeader();
  }

  function addFavoriteData(productId) {
    if (productId) {
      state.favorites[productId] = 1;
    }
  }

  function removeFavoriteProduct(productId) {
    if (productId) {
      if (productId in state.favorites) {
        delete state.favorites[productId];
      }
    }
  }

  function updateFavoriteProduct(payload) {
    state.favProduct = payload;
  }

  function updateShowSnackbar(payload) {
    state.showSnackbar = payload;
  }

  function updateFavoriteData({ product, value, target }) {
    state.favProduct = product;
    state.targetDomEl = target;

    if (value) {
      addFavoriteItem();
    } else {
      removeFavoriteItem();
    }

    if (UserService.isRegistered()) {
      state.favProduct = null;
    }
  }

  function addFavorite(productId) {
    addFavoriteData(productId);

    if (UserService.isRegistered()) {
      const {
        mutate: addFavoriteMutation, //
        onError,
        onDone,
      } = useMutation(gql`
        mutation AddFavorite($productId: ID!) {
          addFavorite(productId: $productId)
        }
      `);

      addFavoriteMutation({ productId });

      onDone(({ data: { addFavorite: currentFavorites } }) => {
        sendAddEvent(productId);
        updateFavorites(currentFavorites);
      });

      onError((error) => {
        console.error(error);
      });
    }
  }

  async function getFavorite() {
    if (!UserService.isRegistered()) {
      updateFavorites([]);
      return;
    }

    try {
      const result = await executeQuery(gql`
        query Favorite {
          favoriteIdGet {
            favorite
          }
        }
      `);

      const { favorite } = result.favoriteIdGet;
      if (favorite) {
        updateFavorites(favorite);
      } else {
        updateFavorites([]);
      }
    } catch (error) {
      updateFavorites([]);
      console.error(error);
      Tracking.sentryCaptureError(error);
    }
  }

  function addManyFavorites(productIds) {
    if (UserService.isRegistered()) {
      productIds.forEach((id) => {
        addFavoriteData(id);
      });

      const {
        mutate: addFavoritesMutation, //
        onError,
        onDone,
      } = useMutation(gql`
        mutation AddFavorites($productIds: [ID!]!) {
          addFavorites(productIds: $productIds)
        }
      `);

      addFavoritesMutation({ productIds });

      onDone(({ data: { addFavorites: currentFavorites } }) => {
        productIds.forEach((id) => {
          sendAddEvent(id);
        });
        updateFavorites(currentFavorites);

        updateShowSnackbar(true);
        setTimeout(() => {
          updateShowSnackbar(false);
        }, 4500);
      });

      onError((error) => {
        console.error(error);
        Tracking.sentryCaptureError(error);
      });

      return {};
    }

    return { unauthorized: true };
  }

  function addFavoriteItem() {
    if (state.favProduct) {
      addFavorite(state.favProduct.hashKey || state.favProduct.id);

      if (UserService.isRegistered()) {
        updateShowSnackbar(true);
        setTimeout(() => {
          updateShowSnackbar(false);
        }, 4500);
      }
    }
  }

  async function removeFavoriteItem() {
    if (state.favProduct) {
      const productId = state.favProduct.hashKey || state.favProduct.id;

      removeFavoriteProduct(productId);

      if (UserService.isRegistered()) {
        const {
          mutate: removeFavoriteMutation, //
          onError,
          onDone,
        } = useMutation(gql`
          mutation RemoveFavorite($productId: ID!) {
            removeFavorite(productId: $productId)
          }
        `);

        removeFavoriteMutation({ productId });

        onDone(({ data: { removeFavorite: remainingFavorites } }) => {
          sendRemoveEvent(productId);
          updateFavorites(remainingFavorites);
        });

        onError((error) => {
          console.error(error);
          Tracking.sentryCaptureError(error);
        });
      }
    }
  }

  return {
    getFavorite,
    addManyFavorites,
    addFavoriteItem,
    removeFavoriteItem,
    updateFavoriteProduct,
    updateFavoriteData,
    favoritesCount: computed(() => Object.keys(state.favorites)?.length ?? 0),
    state,
    ...toRefs(state),
  };
}
